import * as sections from '@shopify/theme-sections';
import ProductForm from '@savchukoleksii/shopify-product-form';
import Swiper, { EffectFade, Navigation, Pagination, Thumbs, Zoom, Keyboard } from 'swiper';
import { membershipWidgetInit } from '@/helpers/membership';
import { updateProductPrices } from '@/scripts/components/product-prices';

export const SECTION_NAME = 'product-new';

const selectors = {
    productForm: '[data-product-form]',
    selectSingleOptionSelector: 'select[data-single-option-selector]',
    option2ValueSelectors: '[data-option2-value]',
    option3ValueSelectors: '[data-option3-value]',
    submitButton: '[data-product-submit]',
    submitButtonLabel: '[data-product-submit-text]',
    productSelect: '[data-product-select]',
    selectedOptionValues: '[data-selected-option-value]',
    selectedColorSwatches: '[data-selected-color-swatch]',
    numberOfAvailableOptions: '[data-options-available-text]',
    imagesContainer: '[data-product-images]',
    mainSlider: '[data-product-images-slider]',
    mainSlide: '[data-main-slide]',
    mainVideoSlides: '[data-main-slide-video]',
    mainSliderPaginationBullets: '[data-product-images-pagination] .swiper-pagination-bullet',
    mainSliderWrapper: '[data-product-images-wrapper-desktop]',
    mainSliderWrapperMobile: '[data-product-images-wrapper-mobile]',
    thumbsSlider: '[data-product-thumbnails-carousel]',
    thumbsWrapper: '[data-product-thumbnails-wrapper]',
    memberPrice: '[data-member-price-tab] [data-product-member-price]',
    memberPriceTab: '[data-member-price-tab="membership"]',
    memberCompareAtPrice: '[data-member-compare-price]',
    productPrice: '[data-product-price]',
    productPriceTab: '[data-member-price-tab="regular"]',
    productCompareAtPrice:
        '[data-member-price-tab] [data-product-compare-price]',
    memberDiscountValue: '[data-member-discount-value]',
    stockState: '[data-stock-state]',
    productJson: '[data-product-json]',
    settingsJson: '[data-settings-json]',
    allImages: '[data-json-product-images]',
}

sections.register(SECTION_NAME, {
    product: null,
    settings: {},
    productForm: null,
    productFormId: null,
    currentVariant: null,
    sliders: {},
    originalThumbnails: '',
    originalImages: '',
    selectedOptions: {
        option1: null,
        option2: null,
        option3: null,
    },

    async onLoad() {
        const { container } = this
        if (!container) {
            return
        }

        this.product = this.getProductJson()

        if (!this.product || !Object.keys(this.product).length) {
            return
        }

        this.settings = this.getSettingsJson()

        this.init()
    },

    onUnload() {
        this.destroy()
    },

    getProductJson() {
        const productJsonElement = this.container.querySelector(
            selectors?.productJson
        )
        if (!productJsonElement) {
            return null
        }

        return JSON.parse(productJsonElement?.innerHTML?.trim() || 'null')
    },

    getSettingsJson() {
        const settingsJsonElement = this.container?.querySelector(
            selectors?.settingsJson
        )
        if (!settingsJsonElement) {
            return {}
        }

        try {
            return JSON.parse(settingsJsonElement?.innerHTML?.trim() || 'null')
        } catch (error) {
            console.log(error)
        }

        return null
    },

    loading(enabled = true) {
        const productForm = this.container.querySelector(selectors.productForm)
        if (!productForm) {
            return
        }

        if (enabled) {
            productForm.classList.add('product-form--loading')
        } else {
            productForm.classList.remove('product-form--loading')
        }
    },

    onFormSubmit(event) {
        console.log(event)
    },

    onQuantityChange(event) {
        console.log(event)
    },

    onPropertyChange(event) {
        console.log(event)
    },

    onOptionChange(event) {
        this.setCurrentVariant(event)
    },

    onSellingPlanChange(event) {
        console.log(event)
    },

    init() {
        if (!this.container) {
            return
        }

        membershipWidgetInit()
        this.initProductForm()
        this.initSlider()
        this.setCurrentVariant()
        this.initAccordions()

        window.initAfterPay()

        this.setSliderHeight()
        window.addEventListener('resize', this.setSliderHeight.bind(this));

        this.loading(false)
    },

    setSliderHeight() {
        const container = this.container;
        if (!container) {
            return;
        }

        const imagesContainer = container.querySelector(selectors.imagesContainer);
        if (!imagesContainer) {
            return;
        }

        if (window.innerWidth < 1025) {
            imagesContainer.style.removeProperty('height');
            return;
        }

        let offset = 0

        const promobar = document.querySelector('[data-promobar]');
        const header = document.querySelector('[data-site-header]');
        
        offset += promobar ? promobar.offsetHeight : 0;
        offset += header ? header.offsetHeight : 0;

        imagesContainer.style.height = `calc(100svh - ${ offset }px)`;
    },

    setCurrentVariant(event = null) {
        if (event) {
            this.currentVariant = event?.dataset?.variant || null;
        } else {
            const params = new URLSearchParams(window.location.search)
            const variant_id = Number(params.get('variant') || 0)
            if (!variant_id) {
                this.currentVariant = this.product.first_available_variant
            } else {
                this.currentVariant = this.product.variants.find(
                    (variant) => variant.id === variant_id
                )
            }
        }

        if (this.currentVariant) {
            this.onVariantChange()
            return
        }

        const option1 = event.dataset?.options[0]?.value
        const option2 = event.dataset?.options[1]?.value
        if (!option1 && !option2) {
            return
        }

        const option1_option2_existing_variant = this.product.variants.find(
            (variant) => variant?.option1 === option1 && variant?.option2 === option2
        )
        if (option1_option2_existing_variant) {
            this.currentVariant = option1_option2_existing_variant
        } else {
            const option1_existing_variant = this.product.variants.find(
                (variant) => variant.option1 === option1
            )
            if (option1_existing_variant) {
                this.currentVariant = option1_existing_variant
            }
        }

        if (this.currentVariant) {
            this.onVariantChange()
            return
        }

        this.currentVariant = this.product.first_available_variant
        this.onVariantChange()
    },

    onVariantChange() {
        this.updateURL()
        this.updateOptionSelectors()
        this.updateSelectedOptionValues()
        this.updateSelectedColorSwatches()
        this.updateProductSelect()
        this.updatePrices()
        this.updateATCButton()
        this.updateStockState()
        this.updateSlider();

        document.dispatchEvent(new Event('drawers:close'));
    },

    updateURL() {
        if (!this.currentVariant.id) {
            return
        }

        const url = new URL(window.location.href)
        url.searchParams.set('variant', this.currentVariant.id)
        window.history.replaceState({}, '', url)
    },

    updateOptionSelectors() {
        const product = this.product;
        if (!product) {
            return
        }

        const variants = product?.variants;
        if (!variants) {
            return
        }

        if (!variants?.length) {
            return
        }

        const currentVariant = this.currentVariant;
        if (!currentVariant) {
            return
        }

        const option1 = currentVariant?.option1;
        if (!option1) {
            return
        }

        const option2ValueSelectors = document.querySelectorAll(
            `[data-form="${this.productFormId}"]${selectors.option2ValueSelectors}`
        )

        if (!option2ValueSelectors?.length) {
            return
        }

        option2ValueSelectors.forEach((el) => el.classList.remove('hide'))

        const existing_option2_values = [
            ...new Set(
                variants
                    ?.filter((variant) => {
                        return variant?.option1 === option1
                    })
                    ?.map((variant) => {
                        return variant?.option2
                    }) || []
            ),
        ]

        option2ValueSelectors.forEach((el) => {
            const value = el?.dataset?.option2Value || ''
            if (!value) {
                return
            }

            if (!existing_option2_values?.includes(value)) {
                el?.classList?.add('hide')

                return
            }

            if (value === currentVariant?.option2) {
                const radio = el.querySelector('input')

                if (radio) {
                    radio.checked = true
                }
            }

            const optionImageContainer = el.querySelector('[data-option-image]')
            if (!optionImageContainer) {
                return
            }

            const alt = `${this.currentVariant.option1} / ${value}`
            const optionImageURL = (this.product.images?.find((img) => {
                return img.alt?.includes(alt)
            }) || this.product.featured_image)?.src

            const optionImage = optionImageContainer?.querySelector('img')

            if (!optionImageURL && !!optionImage) {
                optionImage.remove()

                return
            }

            const optionImageURLSized = `${optionImageURL}&width=225`

            if (optionImage) {
                optionImage.src = optionImageURLSized
                optionImage.alt = alt

                el?.classList?.remove('hide')

                return
            }

            optionImageContainer.innerHTML = `<img
                src="${optionImageURLSized}"
                alt="${alt}"
                width="225"
                height="225"
                class="absolute-img"
            />`

            el?.classList?.remove('hide')
        })

        this.updateAvailableNumberOfOptions(existing_option2_values.length, 2)

        const { option2 } = this.currentVariant
        const option3ValueSelectors = document.querySelectorAll(
            `[data-form="${this.productFormId}"]${selectors.option3ValueSelectors}`
        )
        if (!option2 || !option3ValueSelectors?.length) {
            return
        }

        option3ValueSelectors.forEach((el) => {
            el?.classList?.remove('hide')
        })

        const existing_option3_values = [
            ...new Set(
                this.product.variants
                    .filter((variant) => {
                        return ![
                            variant?.option1 === option1,
                            variant?.option2 === option2,
                        ]?.includes(false)
                    })
                    .map((variant) => {
                        return variant?.option3
                    }) || []
            ),
        ]

        option3ValueSelectors.forEach((el) => {
            const value = el?.dataset?.option3Value || ''

            if (existing_option3_values.includes(value)) {
                if (value === this.currentVariant.option3) {
                    const radio = el.querySelector('input')
                    if (radio) {
                        radio.checked = true
                    }
                }

                const optionImageContainer = el.querySelector(
                    '[data-option-image]'
                )
                if (!optionImageContainer) {
                    return
                }

                const alt = [
                    currentVariant?.option1,
                    currentVariant?.option2,
                    value,
                ]
                    .filter((part) => {
                        return !!part
                    })
                    .join(' / ')

                const optionImageURL = ((
                    variants?.reduce((images, variant) => {
                        const image = variant?.image
                        if (!image) {
                            return images
                        }

                        return [...images, image]
                    }, []) ||
                    product?.images ||
                    []
                )?.find((img) => {
                    return img?.alt?.toLowerCase() === alt?.toLowerCase()
                }) || product?.featured_image)?.src

                const optionImage = optionImageContainer.querySelector('img')
                if (!optionImageURL) {
                    if (optionImage) {
                        optionImage.remove()
                    }

                    return
                }

                const optionImageURLSized = `${optionImageURL}&width=225`

                if (optionImage) {
                    optionImage.src = optionImageURLSized
                    optionImage.alt = alt
                    return
                }

                optionImageContainer.innerHTML = `<img
                    src="${optionImageURLSized}"
                    alt="${alt}"
                    width="225"
                    height="225"
                    class="absolute-img"
                >`

                return
            }

            el?.classList?.add('hide')
        })

        this.updateAvailableNumberOfOptions(existing_option3_values.length, 3)
    },

    updateAvailableNumberOfOptions(numberOfOptions, index) {
        if (!numberOfOptions || !index || !this.productFormId) {
            return
        }

        const el = document.querySelector(
            `[data-form="${this.productFormId}"][data-index="option${index}"]${selectors.numberOfAvailableOptions}`
        )
        if (!el) {
            return
        }

        const option_name =
            this.product?.options
                ?.find((option) => option.position === index)
                ?.name?.toLowerCase() ?? ''
        const s = option_name && numberOfOptions === 1 ? '' : 's'

        el.innerHTML = `${numberOfOptions} ${option_name}${s}`.trim()
    },

    updateSelectedOptionValues() {
        const selectedOptionValues = document.querySelectorAll(
            `[data-form="${this.productFormId}"]${selectors.selectedOptionValues}`
        )
        if (!selectedOptionValues?.length) {
            return
        }

        selectedOptionValues.forEach((el) => {
            const option_index = el.dataset.index
            if (!option_index) {
                return
            }

            const value = this.currentVariant[option_index]
            if (!value) {
                return
            }

            el.innerHTML = value
        })
    },

    updateSelectedColorSwatches() {
        const container = this.container;
        if (!container) {
            return;
        }

        const settings = this.settings;
        if (!settings) {
            return;
        }

        const colors = settings?.colors;
        if (!colors) {
            return;
        }

        if (!colors?.length) {
            return
        }

        const selectedColorSwatches = Array.from(container.querySelectorAll(`${selectors?.selectedColorSwatches}`));
        if (!selectedColorSwatches) {
            return;
        }

        if (!selectedColorSwatches?.length) {
            return
        }

        selectedColorSwatches.forEach((el) => {
            const option_index = el?.dataset?.index
            if (!option_index) {
                return
            }

            const value = this.currentVariant[option_index]
            if (!value) {
                return
            }

            const color = colors.find((color) => {
                return color?.color_name?.toLowerCase() === value?.toLowerCase();
            });

            if (!color) {
                return
            }

            if (!color.hex_color_code) {
                el.style.backgroundImage = 'initial'
                el.style.backgroundColor = '#e2e2e2'
                return
            }

            if (color.color_image_url) {
                el.style.backgroundImage = `url(${color.color_image_url}&width=16)`
            } else {
                el.style.backgroundImage = 'initial'
                el.style.backgroundColor = color.hex_color_code
            }
        })
    },

    updateProductSelect() {
        const productSelect = this.container.querySelector(
            selectors.productSelect
        )
        if (!productSelect) {
            return
        }

        productSelect.value = this.currentVariant.id
    },

    updatePrices() {
        updateProductPrices(this.currentVariant, this.product);
    },

    updateATCButton() {
        const submitButton = this.container.querySelector(selectors?.submitButton);
        if (!submitButton) {
            return;
        }

        const submitButtonLabel = this.container.querySelector(selectors?.submitButtonLabel)
        if (!submitButtonLabel) {
            return
        }

        if (!this.currentVariant) {
            submitButton.classList.add('disabled')
            submitButton.setAttribute('disabled', 'disabled')

            submitButtonLabel.innerHTML = window.theme?.strings?.unavailable || 'Unavailable'

            return;
        }

        if (!this.currentVariant?.available) {
            submitButton.classList.add('disabled');
            submitButton.setAttribute('disabled', 'disabled');
            submitButtonLabel.innerHTML = window.theme?.strings?.soldOut || 'Sold Out'
            return
        }

        submitButton.classList.remove('disabled')
        submitButton.removeAttribute('disabled')
        submitButtonLabel.innerHTML =
            window.theme?.strings?.addToBag || 'Add to Bag'
    },

    updateStockState() {
        const stockState = this.container.querySelector(selectors.stockState)
        if (!stockState) {
            return
        }

        if (!this.currentVariant?.available) {
            stockState.classList.add('hide')
            return
        }

        stockState.classList.remove('hide')
    },

    updateSlider() {
        this?.sliders?.mainSlider?.destroy();
        this?.sliders?.mobileSlider?.destroy();
        this?.sliders?.thumbsSlider?.destroy();

        this.filterMainSlider();
        this.filterThumbnailSlider();

        this.initSlider();

        this.updateSliderPaginationBullets();
    },

    async filterMainSlider() {
        await this.filterMainSliderDesktop();
        await this.filterMainSliderMobile();
    },

    async filterMainSliderMobile() {
        const container = this.container;
        if (!container) {
            return;
        }

        const product = this.product;
        if (!product) {
            return;
        }

        if (!!product?.has_only_default_variant) {
            return
        }

        const sliders = this.sliders;
        if (!sliders) {
            return;
        }

        const mainSlider = sliders?.mainSlider;
        if (!mainSlider) {
            return;
        }

        const mainSliderWrapperMobile = container.querySelector(selectors.mainSliderWrapperMobile);
        if (!mainSliderWrapperMobile) {
            return
        }

        mainSliderWrapperMobile.innerHTML = this.originalImages;

        const images = Array.from(mainSliderWrapperMobile.querySelectorAll('[data-product-image]'));
        if (!images) {
            return;
        }

        if (!images?.length) {
            return;
        }

        let index = 0;
        images.forEach((image) => {
            if (!![
                image?.getAttribute('data-alt')?.indexOf(this.currentVariant.title) >= 0,
                image?.getAttribute('data-alt')?.indexOf('all') >= 0
            ]?.includes(true)) {
                image.removeAttribute('data-swiper-slide-index');

                index++;

                return;
            }

            image.remove();
        });

        const filteredImages = Array.from(mainSliderWrapperMobile.querySelectorAll('[data-product-image]'));

        if (!filteredImages) {
            return;
        }

        if (filteredImages?.length < 2) {
            return;
        }

        const first_image = filteredImages[0];
        const last_image = filteredImages[filteredImages.length - 1];

        if (first_image === last_image) {
            return;
        }

        if (!!last_image) {
            last_image.dataset.swiperSlideDuplicate = "true";

            mainSliderWrapperMobile.prepend(last_image.cloneNode(true));
        }

        if (!!first_image) {
            first_image.dataset.swiperSlideDuplicate = "true";

            mainSliderWrapperMobile.append(first_image.cloneNode(true));
        }
    },

    async filterMainSliderDesktop() {
        const container = this.container;
        if (!container) {
            return;
        }

        const product = this.product;
        if (!product) {
            return;
        }

        if (!!product?.has_only_default_variant) {
            return
        }

        const sliders = this.sliders;
        if (!sliders) {
            return;
        }

        const mainSlider = sliders?.mainSlider;
        if (!mainSlider) {
            return;
        }

        const mainSliderWrapper = container.querySelector(selectors.mainSliderWrapper);
        if (!mainSliderWrapper) {
            return
        }

        mainSliderWrapper.innerHTML = this.originalImages;

        const images = Array.from(mainSliderWrapper.querySelectorAll('[data-product-image]'));
        if (!images) {
            return;
        }

        if (!images?.length) {
            return;
        }

        let index = 0;
        images.forEach((image) => {
            if (!![
                image?.getAttribute('data-alt')?.indexOf(this.currentVariant.title) >= 0,
                image?.getAttribute('data-alt')?.indexOf('all') >= 0
            ]?.includes(true)) {
                image.dataset.swiperSlideIndex = `${index}`;

                index++;

                return;
            }

            image.remove();
        });
    },

    filterThumbnailSlider() {
        const container = this.container;
        if (!container) {
            return;
        }

        const product = this.product;
        if (!product) {
            return;
        }

        if (!!product?.has_only_default_variant) {
            return
        }

        const sliders = this.sliders;
        if (!sliders) {
            return;
        }

        const thumbsWrapper = container.querySelector(selectors.thumbsWrapper)
        if (!thumbsWrapper) {
            return
        }

        thumbsWrapper.innerHTML = this.originalThumbnails

        const thumbnails = Array.from(thumbsWrapper.querySelectorAll('[data-product-thumbnail]'));
        if (!thumbnails) {
            return;
        }

        if (!thumbnails?.length) {
            return;
        }

        let index = 0;
        thumbnails.forEach((thumbnail) => {
            if (!![
                thumbnail.getAttribute('data-alt').indexOf(this.currentVariant.title) >= 0,
                thumbnail.getAttribute('data-alt').indexOf('all') >= 0
            ]?.includes(true)) {
                thumbnail.dataset.swiperSlideIndex = `${index}`;

                index++;

                return;
            }

            thumbnail?.remove()
        });
    },

    initProductForm() {
        const container = this.container;
        if (!container) {
            return;
        }

        this.productForm = container.querySelector(selectors?.productForm)
        if (!this.productForm) {
            return
        }

        const onFormSubmit = this.onFormSubmit.bind(this)
        const onQuantityChange = this.onQuantityChange.bind(this)
        const onPropertyChange = this.onPropertyChange.bind(this)
        const onOptionChange = this.onOptionChange.bind(this)
        const onSellingPlanChange = this.onSellingPlanChange.bind(this)

        this.productFormId = this.productForm.getAttribute('id')

        this.productForm = new ProductForm(this.productForm, this.product, {
            // selectors: {},
            onFormSubmit,
            onQuantityChange,
            onPropertyChange,
            onOptionChange,
            onSellingPlanChange,
        })
    },

    updateSliderPaginationBullets() {
        const container = this.container;
        if (!container) {
            return;
        }

        const videoSlides = Array.from(container.querySelectorAll(
            selectors?.mainSlide
        ));

        if (!videoSlides) {
            return;
        }

        if (!videoSlides?.length) {
            return
        }

        const paginationBullets = Array.from(container?.querySelectorAll(
            selectors?.mainSliderPaginationBullets
        ))

        if (!paginationBullets) {
            return
        }

        if (!paginationBullets?.length) {
            return;
        }

        videoSlides.forEach((videoSlide, index) => {
            const paginationBullet = paginationBullets[index + 1]
            if (!paginationBullet) {
                return
            }

            const slideType = videoSlide?.dataset?.slideType;
            if (!slideType) {
                return;
            }

            if (slideType?.toLowerCase() !== 'video') {
                return;
            }

            paginationBullet?.classList?.add('swiper-pagination-bullet--video')
        })
    },

    initSlider() {
        const container = this.container;
        if (!container) {
            return;
        }

        const mainSlider = container?.querySelector(`[data-product-images-slider-desktop]`);
        const mobileSlider = document.querySelector(`[data-product-images-slider-mobile]`);
        if (!mainSlider) {
            return
        }

        if (!mainSlider) {
            return;
        }

        this.initThumbsSlider()

        const thumbs = this.sliders.thumbsSlider
            ? { swiper: this.sliders.thumbsSlider }
            : null

        const pagination = document.querySelector('[data-product-images-pagination]');

        const swiperOptions = {
            initialSlide: 0,
            slidesPerView: 1,
            spaceBetween: 0,
            draggable: true,
            allowTouchMove: true,
            touchEventsTarget: 'container',
            keyboard: {
                enabled: true,
            },
        };

        const desktopSwiper = new Swiper(mainSlider, {
            ...swiperOptions,
            initialSlide: 0,
            loop: true,
            effect: "fade",
            fadeEffect: {
                crossFade: true,
            },
            zoom: {
                minRatio: 1,
                maxRatio: 1,
                toggle: true,
            },
            breakpoints: {
                1025: {
                    zoom: {
                        minRatio: 1,
                        maxRatio: 1.75,
                        toggle: true,
                    }
                },
            },
            thumbs,
            modules: [
                Zoom,
                Thumbs,
                EffectFade,
                Keyboard
            ],
            on: {
                afterInit(swiper) {
                    const element = swiper?.el;
                    if (!element) {
                        return;
                    }

                    element.addEventListener('click', () => {
                        const zoom = swiper?.zoom;
                        if (!zoom) {
                            return;
                        }

                        zoom.toggle();
                    });

                    const wrapperEl = swiper?.wrapperEl;
                    if (!wrapperEl) {
                        return;
                    }

                    const slides = swiper?.slides;
                    if (!slides) {
                        wrapperEl.dataset.slidesCount = "0";

                        return;
                    }

                    if (!slides?.length) {
                        wrapperEl.dataset.slidesCount = "0";

                        return;
                    }

                    const filteredSlides = slides?.filter((slide) => {
                        return !slide?.dataset?.swiperSlideDuplicate;
                    });

                    if (!filteredSlides) {
                        wrapperEl.dataset.slidesCount = "0";

                        return;
                    }

                    if (!filteredSlides?.length) {
                        wrapperEl.dataset.slidesCount = "0";

                        return;
                    }

                    wrapperEl.dataset.slidesCount = slides.length;
                },
                resize(swiper) {
                    const zoom = swiper?.zoom;
                    if (!zoom) {
                        return;
                    }

                    zoom.out();
                },
                sliderMove(swiper) {
                    const zoom = swiper?.zoom;
                    if (!zoom) {
                        return;
                    }

                    zoom.out();
                },
                slideChange(swiper) {
                    const zoom = swiper?.zoom;
                    if (!zoom) {
                        return;
                    }

                    zoom.out();
                }
            }
        });

        const mobileSwiper = new Swiper(mobileSlider, {
            ...swiperOptions,
            initialSlide: 1,
            effect: "slide",
            centeredSlides: true,
            spaceBetween: 5,
            ...(!!pagination ? {
                pagination: {
                    el: pagination,
                    type: 'bullets',
                    clickable: true,
                    renderBullet(index, className) {
                        const slidesCount = parseInt(mobileSlider?.dataset?.slidesCount) || 0;

                        switch (index) {
                            case 0: {
                                return `<span class="${className} hidden" data-swiper-slide-index="${index}" data-index="${index}"></span>`;
                            }
                            case slidesCount: {
                                return `<span class="${className} hidden" data-swiper-slide-index="${index}" data-index="${index}"></span>`;
                            }
                            default: {
                                return `<span class="${className} ${index === 1 ? ' swiper-pagination-bullet-active' : ''}" data-swiper-slide-index="${index}" data-index="${index}"></span>`;
                            }
                        }
                    }
                }
            } : {}),
            modules: [
                Pagination,
                Keyboard
            ],
            on: {
                afterInit(swiper) {
                    const wrapperEl = swiper?.wrapperEl;
                    if (!wrapperEl) {
                        return;
                    }

                    const slides = swiper?.slides;
                    if (!slides) {
                        wrapperEl.dataset.slidesCount = "0";

                        return;
                    }

                    if (!slides?.length) {
                        wrapperEl.dataset.slidesCount = "0";
                        swiper.slideTo(0);

                        return;
                    }

                    const filteredSlides = slides?.filter((slide) => {
                        return !slide?.dataset?.swiperSlideDuplicate;
                    });

                    if (!filteredSlides) {
                        wrapperEl.dataset.slidesCount = "0";
                        swiper.slideTo(0);

                        return;
                    }

                    if (!filteredSlides?.length) {
                        wrapperEl.dataset.slidesCount = "0";
                        swiper.slideTo(0);

                        return;
                    }

                    wrapperEl.dataset.slidesCount = slides.length;
                },
                reachBeginning(swiper) {
                    const slides = swiper.slides;
                    if (!slides) {
                        return;
                    }

                    const slidesCount = slides?.length;
                    if (!slidesCount) {
                        return;
                    }

                    swiper.slideTo(slides?.length - 2);
                },
                reachEnd(swiper) {
                    const slides = swiper.slides;
                    if (!slides) {
                        return;
                    }

                    const slidesCount = slides?.length;
                    if (!slidesCount) {
                        return;
                    }

                    swiper.slideTo(1);
                }
            }
        });

        this.sliders.mainSlider = desktopSwiper;
        this.sliders.mobileSlider = mobileSwiper;

        const jsonData = document.querySelector(selectors.allImages)
        if (!jsonData) {
            return
        }

        const parsedImages = JSON.parse(jsonData.innerHTML);
        if (!parsedImages) {
            return;
        }

        let images = ''
        let thumbnails = ''

        parsedImages.allImages.forEach((image, i) => {
            images += image
            thumbnails += parsedImages.allThumbs[i]
        });

        this.originalImages = images
        this.originalThumbnails = thumbnails
    },

    initThumbsSlider() {
        const thumbsSlider = this.container.querySelector(
            selectors.thumbsSlider
        )
        if (!thumbsSlider) {
            return
        }

        this.sliders.thumbsSlider = new Swiper(thumbsSlider, {
            initialSlide: 0,
            direction: 'vertical',
            spaceBetween: 5,
            slidesPerView: 4,
            loop: false,
            draggable: true,
            allowTouchMove: true,
            navigation: {
                nextEl: '[data-product-thumbs-next]',
                prevEl: '[data-product-thumbs-prev]',
            },
            modules: [Navigation],
            on: {
                afterInit(swiper) {
                    const wrapperEl = swiper?.wrapperEl;
                    if (!wrapperEl) {
                        return;
                    }

                    const slides = swiper?.slides;
                    if (!slides) {
                        wrapperEl.dataset.slidesCount = "0";

                        return;
                    }

                    if (!slides?.length) {
                        wrapperEl.dataset.slidesCount = "0";

                        return;
                    }

                    const filteredSlides = slides?.filter((slide) => {
                        return !slide?.dataset?.swiperSlideDuplicate;
                    });

                    if (!filteredSlides) {
                        wrapperEl.dataset.slidesCount = "0";

                        return;
                    }

                    if (!filteredSlides?.length) {
                        wrapperEl.dataset.slidesCount = "0";

                        return;
                    }

                    wrapperEl.dataset.slidesCount = slides.length;
                }
            }
        })
    },

    initAccordions() {
        const { container } = this
        if (!container) {
            return
        }

        const accordions = container.querySelectorAll('.accordion-product')
        if (!accordions) {
            return
        }

        accordions.forEach((accordion) => {
            accordion.addEventListener('click', () => {
                if (accordion.hasAttribute('open')) {
                    accordion.removeAttribute('open')
                } else {
                    accordions.forEach((acc) => acc.removeAttribute('open'));
                    accordion.setAttribute('open', '')
                }
            })
        })
    },

    destroy() {
        this.destroyProductForm()
        window.removeEventListener('resize', this.setSliderHeight.bind(this));
    },

    destroyProductForm() {
        const { productForm } = this
        if (!productForm) {
            return
        }

        productForm.destroy()
    },
})

document.addEventListener('DOMContentLoaded', () => {
    sections.load([SECTION_NAME])
})
